import { useEffect, useState } from 'react';

import { Typography } from '@airbus/components-react';

import styles from './index.module.css';

import { useLanguageContextAPI } from '../shared/context/LanguageContext';
import { useUserContextApi } from '../shared/context/UserContext';

import Layout from '../shared/components/layout';
import Spinners from '../shared/components/Spinner';
import ScreenTabs from '../features/landing_page/ScreenTabs';
import DisplayScreens from '../features/landing_page/DisplayScreens';

import { AccessData, ScreenData, UserData, UserDetailsData } from '../shared/types';
import { useAllScreensContext } from '../shared/context/AllScreensDetailsContext';
import { useFeatureFlagEnabled } from 'posthog-js/react';

export default function Home() {
  const {
    accessControlDetails,
    userDetails,
    updateUserDetails,
    acceptDisclaimer,
  }: {
    loginDetails: UserData;
    accessControlDetails: AccessData;
    userDetails: UserDetailsData;
    updateUserDetails: React.Dispatch<UserDetailsData>;
    acceptDisclaimer: boolean;
  } = useUserContextApi();
  const { menuGroup } = useAllScreensContext();
  const { t } = useLanguageContextAPI();
  const pspEnabled = useFeatureFlagEnabled('newPspManagement') === true;
  //   Screens State
  const [noScreenAccessDetails, setNoScreenAccessDetails] = useState<ScreenData>(null);
  const [screenAccessDetails, setScreenAccessDetails] = useState<ScreenData>(null);
  const [accessibleScreens, setAccessibleScreens] = useState<string[]>(null);

  //   Tab State
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const allSubMenusWithParent = (input) => {
    return input.flatMap((screenData) => {
      return screenData.subMenu.map((subMenuData) => {
        return {
          parentAppName: screenData.appName,
          ...subMenuData,
        };
      });
    });
  };

  useEffect(() => {
    if (accessControlDetails) {
      const accessScreens = accessControlDetails.map(
        (item) => item['mbdScreen'].screenInternalName,
      );
      setAccessibleScreens(accessScreens);
    }
  }, [accessControlDetails]); // []

  useEffect(() => {
    if (!accessControlDetails || !accessibleScreens) return;
    setLoading(true);
    const filteredAccessScreens = menuGroup
      .map((item) => {
        const mergedSubMenu = item.subMenu.flatMap((item) => {
          return item.subChildMenu ? [item, ...item.subChildMenu] : [item];
        });
        const uniqueSubMenu = Array.from(
          new Map(mergedSubMenu.map((item) => [item.appNameId, item])).values(),
        );
        const filteredSubMenu = uniqueSubMenu.filter((subMenuItem) =>
          accessibleScreens.includes(subMenuItem?.appNameId),
        );
        return filteredSubMenu.length > 0 && { ...item, subMenu: filteredSubMenu };
      })
      .filter((item) => item);
    const updatedScreensAccess = allSubMenusWithParent(filteredAccessScreens);

    // Filter out PSP Management when feature flag is undefined/false/null
    let finalScreensAccess = updatedScreensAccess;
    if (!pspEnabled) {
      finalScreensAccess = updatedScreensAccess?.filter((screen) => {
        if (screen.appNameId === 'psp_management') {
          return false;
        }

        return true;
      });
    }

    setScreenAccessDetails(finalScreensAccess);

    const filteredNoAccessScreens = menuGroup
      .map((item) => {
        const mergedSubMenu = item.subMenu.flatMap((item) => {
          return item.subChildMenu ? [item, ...item.subChildMenu] : [item];
        });
        const uniqueSubMenu = Array.from(
          new Map(mergedSubMenu.map((item) => [item.appNameId, item])).values(),
        );
        const filteredSubMenu = uniqueSubMenu.filter(
          (subMenuItem) => !accessibleScreens.includes(subMenuItem.appNameId),
        );
        return filteredSubMenu.length > 0 && { ...item, subMenu: filteredSubMenu };
      })
      .filter((item) => item);
    const updatedNoScreensAccess = allSubMenusWithParent(filteredNoAccessScreens);
    setNoScreenAccessDetails(updatedNoScreensAccess);
    setLoading(false);
  }, [accessibleScreens, menuGroup, pspEnabled]);

  useEffect(() => {
    updateUserDetails({ ...userDetails, app_details: null });
  }, []);

  const renderScreenContent = () => {
    if (loading || !screenAccessDetails) {
      return <Spinners />;
    }

    if (!acceptDisclaimer) {
      return <Typography variant='h6'>{t('landing_page.no_accept_disclaimer')}</Typography>;
    }

    if (screenAccessDetails?.length > 0) {
      return (
        <DisplayScreens
          {...{
            selectedTab,
            noScreenAccessDetails,
            screenAccessDetails,
          }}
        />
      );
    }

    return <Typography variant='h6'>{t('landing_page.no_access')}</Typography>;
  };

  return (
    <Layout>
      <div className={styles.gridBox}>
        <ScreenTabs {...{ selectedTab, setSelectedTab, open, setOpen }} />
        {renderScreenContent()}
      </div>
    </Layout>
  );
}
